import { useEffect, useState } from "react"
import { useForm } from "@inertiajs/react"
import { ArrowLeft, CheckCircle2, Lock, Mail, LoaderCircle } from "lucide-react"
import { Button } from "../../../components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card"
import { Input } from "../../../components/ui/input"
import { Label } from "../../../components/ui/label"
import { Link } from "@inertiajs/react"

type ForgotPasswordFormProps = {
  errors?: {
    email?: string[]
  }
  success?: boolean
  email?: string | null
}

export function ForgotPasswordForm({ errors, success = false, email }: ForgotPasswordFormProps) {
  const form = useForm({
    user: {
      email: ""
    }
  })

  const [isSubmitted, setIsSubmitted] = useState(Boolean(success))

  useEffect(() => {
    setIsSubmitted(Boolean(success))
  }, [success])

  return (
    <div className="w-full max-w-md">
      {isSubmitted ? (
        <div className="flex flex-col items-center text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/10">
            <CheckCircle2 className="h-7 w-7 text-emerald-500" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-100">E-mail enviado!</h1>
          <p className="mt-2 text-sm text-slate-400">
            Enviamos um link de recuperacao para{" "}
            <span className="font-medium text-slate-100">{email}</span>
          </p>
          <p className="mt-4 text-sm text-slate-400">
            Verifique sua caixa de entrada e a pasta de spam. O link expira em 1 hora.
          </p>

          <div className="mt-8 flex w-full flex-col gap-3">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                setIsSubmitted(false)
                form.setData("user", { email: "" })
              }}
              type="button"
            >
              Tentar outro e-mail
            </Button>
            <Link href="/users/sign_in" className="w-full">
              <Button className="w-full" type="button">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Voltar para login
              </Button>
            </Link>
          </div>
        </div>
      ) : (
        <Card className="border-slate-800/80 bg-slate-900/70 shadow-2xl shadow-black/30 backdrop-blur">
          <CardHeader className="space-y-2 p-6">
            <div className="flex flex-col items-center gap-2">
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-brand-600/20">
                <Lock className="h-7 w-7 text-brand-500" />
              </div>
              <CardTitle className="text-2xl font-bold tracking-tight text-slate-100">
                Esqueceu a senha?
              </CardTitle>
              <CardDescription className="text-sm text-slate-400">
                Sem problemas! Informe seu e-mail e enviaremos um link para redefinir sua senha.
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <form
              className="flex flex-col gap-5"
              onSubmit={(event) => {
                event.preventDefault()
                form.post("/users/password")
              }}
            >
              <div className="flex flex-col gap-2">
                <Label htmlFor="email">E-mail</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    className="pl-10"
                    value={form.data.user.email}
                    onChange={(e) => form.setData("user", { email: e.target.value })}
                    required
                    autoComplete="email"
                  />
                </div>
                {errors?.email?.[0] && <p className="text-xs text-rose-400">{errors.email[0]}</p>}
              </div>

              <Button type="submit" size="lg" className="w-full" disabled={form.processing}>
                {form.processing ? (
                  <span className="flex items-center gap-2">
                    <LoaderCircle className="h-4 w-4 animate-spin" />
                    Enviando...
                  </span>
                ) : (
                  "Enviar link de recuperacao"
                )}
              </Button>

              <div className="flex items-center justify-center">
                <Link
                  href="/users/sign_in"
                  className="flex items-center justify-center gap-2 text-sm font-medium text-slate-400 transition-colors hover:text-slate-200"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Voltar para login
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

